/**
 * Reading the reason out of a refused chat request.
 *
 * A 403 is not a login problem. `POST /healthcare-chat-v2/sessions/{id}/chat` refuses over the plan's chat cap
 * with a body that names it —
 *
 *     {"detail": {"error": "Your workspace has used all 5 of its chat questions. Ask your Bluepill contact to
 *                           top it up.", "queries_used": 5, "queries_allowed": 5}}
 *
 * — and six chat surfaces threw that away with `if (status === 401 || status === 403) navigate('/auth')`. The user
 * IS authenticated, so /auth bounces them back: reported as the page reloading with no explanation.
 *
 * 401 means "we do not know who you are", where a redirect is right. 403 means "we know, and the answer is no" —
 * a message. This extracts that message.
 */

/** Said when the body carries nothing sayable. Never empty: a blank refusal is a silent dead end. */
export const GENERIC_REFUSAL =
  'That request was not allowed. Ask your Bluepill contact if you think it should have been.';

/** Where FastAPI puts a refusal reason, in the shapes this codebase actually returns. */
const asText = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    for (const key of ['error', 'message', 'detail'] as const) {
      const nested = (value as Record<string, unknown>)[key];
      if (typeof nested === 'string' && nested.trim()) return nested.trim();
    }
  }
  return null;
};

/**
 * The human-readable reason this response was refused, or `GENERIC_REFUSAL`.
 *
 * Always returns something sayable. `String(detail)` on a dict yields "[object Object]", which is how a refusal
 * becomes a bug report rather than an explanation.
 */
export const refusalMessage = async (res: Response): Promise<string> => {
  try {
    const body: unknown = await res.json();
    const detail =
      body && typeof body === 'object' ? (body as Record<string, unknown>).detail : undefined;
    return asText(detail) ?? asText(body) ?? GENERIC_REFUSAL;
  } catch {
    // Not JSON at all — a proxy error page, or an empty body.
    return GENERIC_REFUSAL;
  }
};
